import React from 'react';
import { Button } from '@components';
import { Card, Form, Row, Col, ButtonGroup, InputGroup } from 'react-bootstrap';
import { Formik } from 'formik';
import * as yup from 'yup';
import { useLocation, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Get, Put } from '@app/utils/httpProvider';
import { LOPDAOTAOLIENTUC_API, DONVI_API, DOITUONG_API } from '@app/utils/apiUrl';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import * as moment from 'moment';
import { 
    messageError, 
    messageToast, 
    status200,
    trangThaiDotTuyenSinhValue,
    objectTrangThaiDotTuyenSinh,
    loaiTuyenSinhValue,
    objectLoaiTuyenSinh,
    ID_EMPTY,
    styleSelectDefault,
    styleSelectError
} from '../../../../../utils/define';
import { URI_INTERNAL } from '../../../../../utils/pathLocations';
import HorizontalFieldsRow from '@app/components/common/HorizontalFieldsRow';
import Select from 'react-select';
import Editor from '../../../../../components/editor/Editor';
import DoiTuongMienGiamFields, { parseDoiTuongMienGiam, stringifyDoiTuongMienGiam } from '../dotTuyenSinh/DoiTuongMienGiamFields';

const schema = yup.object().shape({
    tenKhoaHoc: yup.string().required(messageError.emptyMessage).max(255, 'Tên khóa học không được vượt quá 255 ký tự'),
    khoaDeXuatId: yup.string().nullable(),
    loaiTuyenSinh: yup.string().required(messageError.emptyMessage),
    doiTuongIds: yup.array().min(1, messageError.emptyMessage).nullable(),
    soQuyetDinh: yup.string().nullable(),
    mucDichKhoaHoc: yup.string().nullable(),
    thoiGianKhoaHoc: yup.number().min(1, 'Thời gian học phải lớn hơn 0').nullable(),
    noiDungKhoaHoc: yup.string().nullable(),
    soLuongHocVien: yup.number().min(1, 'Số lượng học viên phải lớn hơn 0').nullable(),
    hocPhi: yup.number().min(0, 'Học phí không được nhỏ hơn 0').nullable(),
    ngayKhaiGiang: yup.string().required(messageError.emptyMessage),
    ngayBeGiang: yup.date().nullable().test('is-greater', 'Ngày bế giảng phải lớn hơn hoặc bằng ngày khai giảng', function(value) {
        const { ngayKhaiGiang } = this.parent;
        if (!value || !ngayKhaiGiang) return true;
        const date1 = new Date(value);
        const date2 = new Date(ngayKhaiGiang);
        if (isNaN(date1) || isNaN(date2)) return true;
        return date1.setHours(0, 0, 0, 0) >= date2.setHours(0, 0, 0, 0);
    }),
    ngayBatDauNhanHoSo: yup.date().nullable(),
    ngayKetThucNhanHoSo: yup.date().nullable().test('is-greater', 'Ngày kết thúc nhận hồ sơ phải lớn hơn hoặc bằng ngày bắt đầu nhận', function(value) {
        const { ngayBatDauNhanHoSo } = this.parent;
        if (!value || !ngayBatDauNhanHoSo) return true;
        const date1 = new Date(value);
        const date2 = new Date(ngayBatDauNhanHoSo);
        if (isNaN(date1) || isNaN(date2)) return true;
        return date1.setHours(0, 0, 0, 0) >= date2.setHours(0, 0, 0, 0);
    }),
    trangThaiDotTuyenSinh: yup.string().required(messageError.emptyMessage),
    doiTuongMienGiams: yup.array().when('coDoiTuongMienGiam', {
        is: true,
        then: yup.array().of(yup.object().shape({ tenDoiTuong: yup.string().trim().required('Vui lòng nhập tên đối tượng').max(255, 'Tên đối tượng không được vượt quá 255 ký tự') })).min(1, 'Vui lòng thêm ít nhất một đối tượng')
    }),
});

const LopDaoTaoLienTucEdit = () => {
    const history = useHistory();
    const queryClient = useQueryClient();
    const location = useLocation();
    const params = new URLSearchParams(location.search.substring(1));
    const itemId = params.get('id');

    const itemQuery = useQuery(['lopDaoTaoLienTucData', itemId], () => Get(LOPDAOTAOLIENTUC_API.GET_ITEM, itemId), {
        enabled: !!itemId
    });

    const { data: donViData } = useQuery(
        ['donViDataLopLienTuc'],
        () => Get(DONVI_API.GET_PAGING, null, { PageIndex: 1, PageSize: 1000 }),
        { keepPreviousData: true }
    );
    const listDonVi = donViData?.data?.$values || donViData?.data || [];

    const { data: doiTuongData } = useQuery(
        ['doiTuongDataLopLienTuc'],
        () => Get(DOITUONG_API.GET_PAGING, null, { PageIndex: 1, PageSize: 1000 }),
        { keepPreviousData: true }
    );
    const listDoiTuong = doiTuongData?.data?.$values || doiTuongData?.data || [];

    const editMutation = useMutation((value) => Put(LOPDAOTAOLIENTUC_API.UPDATE_ITEM, itemId, value));

    const handleEdit = (values) => {
        const { coDoiTuongMienGiam, doiTuongMienGiams, ...requestValues } = values;
        const model = {
            ...requestValues,
            loaiTuyenSinh: values.loaiTuyenSinh,
            thoiGianKhoaHoc: values.thoiGianKhoaHoc === '' ? null : values.thoiGianKhoaHoc,
            soLuongHocVien: values.soLuongHocVien === '' ? null : values.soLuongHocVien,
            hocPhi: values.hocPhi === '' ? 0 : values.hocPhi,
            ngayKhaiGiang: values.ngayKhaiGiang || null,
            ngayBeGiang: values.ngayBeGiang || null,
            ngayBatDauNhanHoSo: values.ngayBatDauNhanHoSo || null,
            ngayKetThucNhanHoSo: values.ngayKetThucNhanHoSo || null,
            khoaDeXuatId: values.khoaDeXuatId === '' ? null : values.khoaDeXuatId,
            doiTuongIds: values.doiTuongIds && values.doiTuongIds.length > 0 ? values.doiTuongIds.join(',') : null,
            doiTuongMienGiam: coDoiTuongMienGiam ? stringifyDoiTuongMienGiam(doiTuongMienGiams) : '',
        };

        editMutation.mutate(model, {
            onSuccess: (res) => {
                if (res.status !== status200) {
                    toast.error(messageToast.messageToastUpdateError);
                } else {
                    toast.success(messageToast.messageToastUpdateSuccess);
                    queryClient.setQueryData(['lopDaoTaoLienTucData', itemId], model);
                    history.push(URI_INTERNAL.LOPDAOTAOLIENTUC_PATH);
                }
            },
            onError: (err) => {
                toast.error(err?.message || 'Đã có lỗi xảy ra, vui lòng thử lại !');
            }
        });
    };

    if (itemQuery.isLoading) {
        return <div>Đang tải....</div>;
    }

    if (itemQuery.isError || !itemQuery.data) {
        return <div>Có lỗi xảy ra hoặc không tìm thấy dữ liệu</div>;
    }

    const currentData = itemQuery.data;

    const initialTrangThai = currentData.trangThaiDotTuyenSinh;
    const initialLoai = currentData.loaiTuyenSinh;
    const initialDoiTuongMienGiams = parseDoiTuongMienGiam(currentData.doiTuongMienGiam ?? '');

    return (
        <Card>
            <Card.Header as="h5">Sửa Lớp Đào tạo liên tục</Card.Header>
            <Card.Body>
                <Formik
                    enableReinitialize
                    validationSchema={schema}
                    onSubmit={(values) => handleEdit(values)}
                    initialValues={{
                        tenKhoaHoc: currentData.tenKhoaHoc ?? '',
                        loaiTuyenSinh: initialLoai ?? loaiTuyenSinhValue.tuyenSinhDTLT,
                        doiTuongIds: currentData.doiTuongIds ? currentData.doiTuongIds.split(',').filter(x => x) : [],
                        khoaDeXuatId: currentData.khoaDeXuatId ?? '',
                        soQuyetDinh: currentData.soQuyetDinh ?? '',
                        mucDichKhoaHoc: currentData.mucDichKhoaHoc ?? '',
                        thoiGianKhoaHoc: currentData.thoiGianKhoaHoc ?? '',
                        noiDungKhoaHoc: currentData.noiDungKhoaHoc ?? '',
                        soLuongHocVien: currentData.soLuongHocVien ?? '',
                        hocPhi: currentData.hocPhi ?? 0,
                        ngayKhaiGiang: currentData.ngayKhaiGiang ? moment(currentData.ngayKhaiGiang).format('YYYY-MM-DD') : '',
                        ngayBeGiang: currentData.ngayBeGiang ? moment(currentData.ngayBeGiang).format('YYYY-MM-DD') : '',
                        ngayBatDauNhanHoSo: currentData.ngayBatDauNhanHoSo ? moment(currentData.ngayBatDauNhanHoSo).format('YYYY-MM-DD') : '',
                        ngayKetThucNhanHoSo: currentData.ngayKetThucNhanHoSo ? moment(currentData.ngayKetThucNhanHoSo).format('YYYY-MM-DD') : '',
                        trangThaiDotTuyenSinh: initialTrangThai ?? trangThaiDotTuyenSinhValue.sapMo,
                        coDoiTuongMienGiam: initialDoiTuongMienGiams.length > 0,
                        doiTuongMienGiams: initialDoiTuongMienGiams,
                    }}
                >
                    {({ handleSubmit, handleChange, values, errors, setFieldValue }) => (
                        <Form noValidate onSubmit={handleSubmit}>
                            <HorizontalFieldsRow
                                fields={[
                                    {
                                        label: 'Tên khóa học',
                                        name: 'tenKhoaHoc',
                                        component: (
                                            <>
                                                <Form.Control type="text" name="tenKhoaHoc" value={values.tenKhoaHoc} onChange={handleChange} />
                                                {errors.tenKhoaHoc && <div className="text-danger small mt-1">{errors.tenKhoaHoc}</div>}
                                            </>
                                        ),
                                        required: true
                                    },
                                    {
                                        label: 'Loại tuyển sinh',
                                        name: 'loaiTuyenSinh',
                                        component: (
                                            <Form.Control
                                                as="select"
                                                name="loaiTuyenSinh"
                                                value={values.loaiTuyenSinh}
                                                onChange={handleChange}
                                            >
                                                {objectLoaiTuyenSinh.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                            </Form.Control>
                                        ),
                                        error: errors.loaiTuyenSinh,
                                        required: true
                                    },
                                ]}
                            />
                            <HorizontalFieldsRow
                                fields={[
                                    {
                                        label: 'Khoa đề xuất',
                                        name: 'khoaDeXuatId',
                                        component: (
                                            <Select
                                                name="khoaDeXuatId"
                                                placeholder="-- Chọn Khoa --"
                                                value={listDonVi.find(x => x.id === values.khoaDeXuatId) ? { label: listDonVi.find(x => x.id === values.khoaDeXuatId).tenDonVi, value: values.khoaDeXuatId } : null}
                                                options={listDonVi.map(opt => ({ label: opt.tenDonVi, value: opt.id }))}
                                                onChange={(opt) => {
                                                    setFieldValue('khoaDeXuatId', opt ? opt.value : '');
                                                }}
                                                isClearable
                                                menuPortalTarget={document.body}
                                                styles={{
                                                    ...(errors.khoaDeXuatId ? styleSelectError : styleSelectDefault),
                                                    menuPortal: base => ({ ...base, zIndex: 9999 })
                                                }}
                                            />
                                        ),
                                        error: errors.khoaDeXuatId
                                    },
                                    {
                                        label: 'Số thông báo tuyển sinh',
                                        name: 'soQuyetDinh',
                                        component: (
                                            <>
                                                <Form.Control type="text" name="soQuyetDinh" value={values.soQuyetDinh} onChange={handleChange} />
                                                {errors.soQuyetDinh && <div className="text-danger small mt-1">{errors.soQuyetDinh}</div>}
                                            </>
                                        )
                                    },
                                ]}
                            />
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="font-weight-bold">Mục đích khóa học</Form.Label>
                                        <Editor
                                            name="mucDichKhoaHoc"
                                            value={values.mucDichKhoaHoc}
                                            onChange={(val) => setFieldValue('mucDichKhoaHoc', val)}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="font-weight-bold">Nội dung khóa học</Form.Label>
                                        <Editor
                                            name="noiDungKhoaHoc"
                                            value={values.noiDungKhoaHoc}
                                            onChange={(val) => setFieldValue('noiDungKhoaHoc', val)}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <HorizontalFieldsRow
                                fields={[
                                    {
                                        label: 'Đối tượng',
                                        name: 'doiTuongIds',
                                        component: (
                                            <Select
                                                isMulti
                                                name="doiTuongIds"
                                                placeholder="-- Chọn Đối tượng --"
                                                value={values.doiTuongIds ? values.doiTuongIds.map(v => {
                                                    const item = listDoiTuong.find(x => x.id === v);
                                                    return { label: item ? item.tenDoiTuong : '', value: v };
                                                }) : []}
                                                options={listDoiTuong.map(opt => ({ label: opt.tenDoiTuong, value: opt.id }))}
                                                onChange={(opts) => {
                                                    setFieldValue('doiTuongIds', opts ? opts.map(x => x.value) : []);
                                                }}
                                                menuPortalTarget={document.body}
                                                styles={{
                                                    ...(errors.doiTuongIds ? styleSelectError : styleSelectDefault),
                                                    menuPortal: base => ({ ...base, zIndex: 9999 })
                                                }}
                                            />
                                        ),
                                        error: errors.doiTuongIds,
                                        required: true
                                    },
                                    {
                                        label: 'Số lượng học viên',
                                        name: 'soLuongHocVien',
                                        component: (
                                            <>
                                                <Form.Control type="number" name="soLuongHocVien" value={values.soLuongHocVien} onChange={handleChange} />
                                                {errors.soLuongHocVien && <div className="text-danger small mt-1">{errors.soLuongHocVien}</div>}
                                            </>
                                        )
                                    },
                                ]}
                            />
                            <HorizontalFieldsRow
                                fields={[
                                    {
                                        label: 'Thời gian khóa học',
                                        name: 'thoiGianKhoaHoc',
                                        component: (
                                            <InputGroup>
                                                <Form.Control
                                                    type="number"
                                                    name="thoiGianKhoaHoc"
                                                    value={values.thoiGianKhoaHoc}
                                                    onChange={handleChange}
                                                />
                                                <InputGroup.Text>tháng</InputGroup.Text>
                                            </InputGroup>
                                        ),
                                        error: errors.thoiGianKhoaHoc
                                    },
                                    {
                                        label: 'Học phí (VNĐ)',
                                        name: 'hocPhi',
                                        component: (
                                            <>
                                                <Form.Control type="number" name="hocPhi" value={values.hocPhi} onChange={handleChange} />
                                                {errors.hocPhi && <div className="text-danger small mt-1">{errors.hocPhi}</div>}
                                            </>
                                        )
                                    },
                                ]}
                            />
                            <HorizontalFieldsRow
                                fields={[
                                    {
                                        label: 'Ngày khai giảng',
                                        name: 'ngayKhaiGiang',
                                        component: (
                                            <>
                                                <Form.Control type="date" name="ngayKhaiGiang" value={values.ngayKhaiGiang} onChange={handleChange} />
                                                {errors.ngayKhaiGiang && <div className="text-danger small mt-1">{errors.ngayKhaiGiang}</div>}
                                            </>
                                        ),
                                        required: true
                                    },
                                    {
                                        label: 'Ngày bế giảng',
                                        name: 'ngayBeGiang',
                                        component: (
                                            <>
                                                <Form.Control type="date" name="ngayBeGiang" value={values.ngayBeGiang} onChange={handleChange} />
                                                {errors.ngayBeGiang && <div className="text-danger small mt-1">{errors.ngayBeGiang}</div>}
                                            </>
                                        )
                                    },
                                ]}
                            />
                            <HorizontalFieldsRow
                                fields={[
                                    {
                                        label: 'Ngày nhận hồ sơ',
                                        name: 'ngayBatDauNhanHoSo',
                                        component: (
                                            <>
                                                <Form.Control type="date" name="ngayBatDauNhanHoSo" value={values.ngayBatDauNhanHoSo} onChange={handleChange} />
                                                {errors.ngayBatDauNhanHoSo && <div className="text-danger small mt-1">{errors.ngayBatDauNhanHoSo}</div>}
                                            </>
                                        )
                                    },
                                    {
                                        label: 'Ngày kết thúc nhận',
                                        name: 'ngayKetThucNhanHoSo',
                                        component: (
                                            <>
                                                <Form.Control type="date" name="ngayKetThucNhanHoSo" value={values.ngayKetThucNhanHoSo} onChange={handleChange} />
                                                {errors.ngayKetThucNhanHoSo && <div className="text-danger small mt-1">{errors.ngayKetThucNhanHoSo}</div>}
                                            </>
                                        )
                                    },
                                ]}
                            />
                            <HorizontalFieldsRow
                                fields={[
                                    {
                                        label: 'Trạng thái',
                                        name: 'trangThaiDotTuyenSinh',
                                        component: (
                                            <Form.Control
                                                as="select"
                                                name="trangThaiDotTuyenSinh"
                                                value={values.trangThaiDotTuyenSinh}
                                                onChange={handleChange}
                                            >
                                                {objectTrangThaiDotTuyenSinh.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                            </Form.Control>
                                        ),
                                        error: errors.trangThaiDotTuyenSinh
                                    },
                                    null,
                                ]}
                            />
                            <DoiTuongMienGiamFields values={values} errors={errors} setFieldValue={setFieldValue} />
                            <Form.Group as={Row} className="mb-3">
                                <Col md={{ span: 10, offset: 2 }}>
                                    <ButtonGroup className="mb-2">
                                        <Button type="submit" theme="primary">
                                            Cập nhật
                                        </Button>
                                        &nbsp;&nbsp;&nbsp;
                                        <Button onClick={() => history.push(URI_INTERNAL.LOPDAOTAOLIENTUC_PATH)} theme="secondary">
                                            Hủy
                                        </Button>
                                    </ButtonGroup>
                                </Col>
                            </Form.Group>
                        </Form>
                    )}
                </Formik>
            </Card.Body>
        </Card>
    );
};

export default LopDaoTaoLienTucEdit;
